"use client"

import type { ToasterProps } from "sonner"
import { Toaster as Sonner } from "sonner"

// React Toastify style icons
const ErrorIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="#e74c3c">
    <path d="M12 0a12 12 0 1012 12A12.014 12.014 0 0012 0zm6.927 8.2l-6.845 9.289a1.011 1.011 0 01-1.43.188l-4.888-3.908a1 1 0 111.25-1.562l4.076 3.261 6.227-8.451a1 1 0 111.61 1.183z"></path>
  </svg>
)

const SuccessIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="#07bc0c">
    <path d="M12 0a12 12 0 1012 12A12.014 12.014 0 0012 0zm6.927 8.2l-6.845 9.289a1.011 1.011 0 01-1.43.188l-4.888-3.908a1 1 0 111.25-1.562l4.076 3.261 6.227-8.451a1 1 0 111.61 1.183z"></path>
  </svg>
)

const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <>
      <style>{`
        @keyframes toastProgress {
          from {
            transform: scaleX(1);
          }
          to {
            transform: scaleX(0);
          }
        }
        
        [data-sonner-toaster] {
          --toast-bg: #ffffff;
          --toast-border: #e0e0e0;
          --toast-text: #757575;
          --success-color: #07bc0c;
          --error-color: #e74c3c;
        }
        
        [data-sonner-toaster] [data-sonner-toast] {
          background: var(--toast-bg) !important;
          border: none !important;
          border-radius: 4px !important;
          box-shadow: 0 1px 10px 0 rgba(0,0,0,.1), 0 2px 15px 0 rgba(0,0,0,.05) !important;
          padding: 8px !important;
          font-family: var(--access-font-family, "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif) !important;
          position: relative !important;
          overflow: hidden !important;
          min-height: 64px !important;
          cursor: pointer !important;
        }
        
        [data-sonner-toaster] [data-sonner-toast] [data-content] {
          display: flex !important;
          align-items: center !important;
          gap: 12px !important;
          padding: 8px !important;
        }
        
        [data-sonner-toaster] [data-sonner-toast] [data-title] {
          color: var(--toast-text) !important;
          font-size: 14px !important;
          font-weight: 400 !important;
          line-height: 1.5 !important;
        }
        
        [data-sonner-toaster] [data-sonner-toast] [data-icon] {
          flex-shrink: 0 !important;
          margin: 0 !important;
        }
        
        /* Progress bar for all toasts */
        [data-sonner-toaster] [data-sonner-toast]::after {
          content: "" !important;
          position: absolute !important;
          bottom: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 5px !important;
          transform-origin: left !important;
          animation: toastProgress 5000ms linear forwards !important;
        }
        
        /* Pause progress bar on hover */
        [data-sonner-toaster] [data-sonner-toast]:hover::after {
          animation-play-state: paused !important;
        }
        
        /* Success toast progress bar */
        [data-sonner-toaster] [data-sonner-toast][data-type="success"]::after {
          background: linear-gradient(to right, rgba(7, 188, 12, 0.7), rgba(7, 188, 12, 0.7)) !important;
        }
        
        /* Error toast progress bar */
        [data-sonner-toaster] [data-sonner-toast][data-type="error"]::after {
          background: linear-gradient(to right, rgba(231, 76, 60, 0.7), rgba(231, 76, 60, 0.7)) !important;
        }
        
        /* Default/info toast progress bar */
        [data-sonner-toaster] [data-sonner-toast]:not([data-type])::after,
        [data-sonner-toaster] [data-sonner-toast][data-type="default"]::after {
          background: linear-gradient(to right, rgba(52, 152, 219, 0.7), rgba(52, 152, 219, 0.7)) !important;
        }
        
        /* Hide close button */
        [data-sonner-toaster] [data-sonner-toast] [data-close-button] {
          display: none !important;
        }
      `}</style>
      <Sonner
        className="toaster group"
        position="top-center"
        duration={5000}
        toastOptions={{
          classNames: {
            toast: "group toast",
            title: "text-sm font-normal",
          },
        }}
        icons={{
          error: <ErrorIcon />,
          success: <SuccessIcon />,
        }}
        closeButton={false}
        {...props}
      />
    </>
  )
}

export { Toaster }
